import { Alert, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { InvitationDetails } from 'api-client';

interface InvitationCardProps {
  invitation: InvitationDetails;
  pending: boolean;
  error: string | null;
  success: boolean;
  onAccept: () => void;
}
export default function InvitationCard({ invitation: invite, pending, error, success, onAccept }: InvitationCardProps) {
  return (
    <Paper component="article" sx={{ p: { xs: 2, sm: 4 } }}>
      <Stack spacing={2} alignItems="flex-start">
        <Typography component="h1" variant="h4">
          Team Invitation
        </Typography>
        <Typography>
          You've been invited to join <strong>{invite.teamName}</strong> by {invite.invitedBy}.
        </Typography>
        <Typography sx={{ overflowWrap: 'anywhere' }}>
          Invitation sent to: <strong>{invite.email}</strong>
        </Typography>
        <Chip variant="outlined" label={invite.role === 'admin' ? 'Administrator' : 'Member'} />
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">You successfully joined {invite.teamName}.</Alert>}
        {invite.alreadyMember ? (
          <>
            {!success && <Alert severity="info">You're already a member of this team.</Alert>}
            <Button href={invite.dashboardUrl} variant="contained">
              Continue to Team
            </Button>
          </>
        ) : invite.isAccepted ? (
          <Alert severity="info">
            This invitation has already been accepted. Ask your team administrator for a new invitation.
          </Alert>
        ) : !invite.isAuthenticated ? (
          <>
            <Typography>Sign in or create an account to accept this invitation.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button href={invite.loginUrl} variant="contained">
                Sign In to Accept
              </Button>
              <Button href={invite.signupUrl} variant="outlined">
                Create Account to Accept
              </Button>
            </Stack>
          </>
        ) : invite.canAccept ? (
          <Button
            onClick={onAccept}
            variant="contained"
            disabled={pending}
            startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {pending ? 'Accepting…' : 'Accept Invitation'}
          </Button>
        ) : (
          <>
            <Alert severity="warning">
              {invite.userOwnsEmail
                ? 'Verify the invited email address before accepting.'
                : 'This invitation was sent to a different email address. Add and verify it on your account, or sign in with the invited account.'}
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button href={invite.emailUrl} variant="contained">
                {invite.userOwnsEmail ? 'Verify Email' : 'Add Email'}
              </Button>
              <Button href={invite.logoutUrl}>Sign Out</Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}
